/**
 * ForesightRenderSystem — enemy attack-arc preview overlay system.
 *
 * Foresight (foresight_passive) shows a ground arc preview before an enemy's
 * attack fires, giving the player time to dodge. Oracle (oracle) extends this
 * to a full tactical overlay showing patrol routes and aggro states.
 *
 * The system manages the lifetime of active arc previews and the Oracle
 * overlay flag. Actual Phaser Graphics rendering is injected via callbacks
 * so this module stays free of Phaser dependencies and testable in the sim
 * harness.
 *
 * Rendering contract:
 * - `onShowArc`: called when a new arc preview starts.
 * - `onExpireArc`: called when an arc preview expires.
 * - `onShowOracleOverlay` / `onHideOracleOverlay`: Oracle mode toggles.
 *
 * Enemy AI systems that enter wind-up state call showArc() when
 * foresight_passive is unlocked. The ForesightPassiveSkillEffect registers
 * the hook into the enemy attack-start event pipeline.
 *
 * showExtendedArcs(): activated by Deathmark (astral_projection_upgrade_2).
 * While an entity is in extended-arc mode, every attack wind-up triggers an
 * arc preview for the duration of the Deathmark window.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § ForesightRenderSystem
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ArcEntry {
  /** Entity whose attack is being previewed. */
  readonly entityId: string;
  /** World position of the attack origin. */
  readonly position: { x: number; y: number };
  /** Facing angle in radians. */
  readonly attackDirection: number;
  /** Total display duration in ms. */
  readonly totalMs: number;
  /** Remaining display time in ms. Counts down. */
  remainingMs: number;
}

/** Render callbacks injected at construction. Null-safe — never throws. */
export interface ForesightRenderCallbacks {
  onShowArc?(entry: ArcEntry): void;
  onExpireArc?(entityId: string): void;
  onShowOracleOverlay?(): void;
  onHideOracleOverlay?(): void;
}

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class ForesightRenderSystem {
  private readonly _arcs = new Map<string, ArcEntry>();
  private _oracleOverlayActive = false;
  private readonly _callbacks: ForesightRenderCallbacks;
  /**
   * Extended-arc timers: entityId → remaining ms.
   * Set by Deathmark (astral_projection_upgrade_2) — tracked enemies show
   * attack arcs continuously for the specified window.
   */
  private readonly _extendedArcTimers = new Map<string, number>();

  constructor(callbacks: ForesightRenderCallbacks = {}) {
    this._callbacks = callbacks;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Show a ground arc preview for the named entity.
   * If an arc for this entity already exists it is replaced.
   *
   * @param entityId    Unique enemy identifier.
   * @param position    World-space origin of the attack.
   * @param attackDirection  Facing direction in radians.
   * @param durationMs  How long the preview persists (400ms base, 600ms with Foreshadow).
   */
  showArc(
    entityId: string,
    position: { x: number; y: number },
    attackDirection: number,
    durationMs: number,
  ): void {
    const entry: ArcEntry = {
      entityId,
      position: { x: position.x, y: position.y },
      attackDirection,
      totalMs: durationMs,
      remainingMs: durationMs,
    };
    this._arcs.set(entityId, entry);
    this._callbacks.onShowArc?.(entry);
  }

  /**
   * Enable the Oracle tactical overlay.
   * Enables patrol-route and aggro-state rendering on all tracked enemies.
   */
  showOracleOverlay(_enemies: Iterable<unknown>): void {
    if (!this._oracleOverlayActive) {
      this._oracleOverlayActive = true;
      this._callbacks.onShowOracleOverlay?.();
    }
  }

  /** Disable the Oracle tactical overlay. */
  hideOracleOverlay(): void {
    if (this._oracleOverlayActive) {
      this._oracleOverlayActive = false;
      this._callbacks.onHideOracleOverlay?.();
    }
  }

  /** Whether the Oracle overlay is currently active. */
  get isOracleOverlayActive(): boolean {
    return this._oracleOverlayActive;
  }

  /** Read-only snapshot of current arc entries (for testing / rendering). */
  getActiveArcs(): ReadonlyMap<string, Readonly<ArcEntry>> {
    return this._arcs;
  }

  /**
   * Enable continuous attack-arc rendering for a set of enemies for a window.
   *
   * Called by AstralProjectionSkillEffect.onDeactivate() when Deathmark
   * (astral_projection_upgrade_2) is unlocked. While an entity is in this
   * mode, the enemy AI wind-up system should call showArc() on every
   * wind-up start (not just the first one).
   *
   * @param entityIds  Enemy IDs to track.
   * @param durationMs How long the extended-arc window lasts (10000ms for Deathmark).
   */
  showExtendedArcs(entityIds: Iterable<string>, durationMs: number): void {
    for (const entityId of entityIds) {
      this._extendedArcTimers.set(entityId, durationMs);
    }
  }

  /**
   * Whether an entity is currently in Deathmark extended-arc mode.
   * Enemy AI systems query this to decide whether to call showArc() on each wind-up.
   */
  hasExtendedArc(entityId: string): boolean {
    const timer = this._extendedArcTimers.get(entityId);
    return timer !== undefined && timer > 0;
  }

  /** Read-only snapshot of which entities are in extended-arc mode and their remaining ms. */
  getExtendedArcTimers(): ReadonlyMap<string, number> {
    return this._extendedArcTimers;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Per-frame: decay arc timers and extended-arc timers; remove expired entries.
   * @param delta Frame delta in ms.
   */
  update(delta: number): void {
    for (const [id, entry] of this._arcs) {
      entry.remainingMs -= delta;
      if (entry.remainingMs <= 0) {
        this._arcs.delete(id);
        this._callbacks.onExpireArc?.(id);
      }
    }
    for (const [id, remaining] of this._extendedArcTimers) {
      const next = remaining - delta;
      if (next <= 0) {
        this._extendedArcTimers.delete(id);
      } else {
        this._extendedArcTimers.set(id, next);
      }
    }
  }

  /** Release all arc state and overlay. */
  destroy(): void {
    this._arcs.clear();
    this._oracleOverlayActive = false;
    this._extendedArcTimers.clear();
  }
}
